import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { getDataAPI, getEffectiveUserId, postDataAPI } from '../lib/api'
import Button from '../components/ui/Button'
import { makeCacheKey, readSessionCache, writeSessionCache } from '../lib/sessionCache'
import { parseLocalDate, toLocalDateKey } from '../lib/dateUtils'
import { useUserPermissions } from '../hooks/useUserPermissions'

interface Show {
  id: string
  date?: string
  perf_date?: string
  title?: string
  event_title?: string
  venue?: string
  city?: string
  status?: string
}

interface Task {
  id: string
  title: string
  description?: string
  due_date?: string
  status: string
  priority?: string
}

interface GoogleCalendarEvent {
  id: string
  summary: string
  start: { dateTime?: string; date?: string }
  end: { dateTime?: string; date?: string }
  location?: string
  description?: string
  organizer?: { email?: string; displayName?: string }
  calendarId?: string
}

interface GoogleCalendar {
  id: string
  summary: string
  description?: string
  backgroundColor?: string
  primary?: boolean
  accessRole?: string
}

interface ScheduleItem {
  id: string
  title: string
  date: string
  time?: string
  type: 'show' | 'task' | 'google'
  venue?: string
  city?: string
  status?: string
  priority?: string
  location?: string
}

type ViewMode = 'list' | 'calendar'

const asRecord = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : null

const toStr = (v: unknown): string | undefined => (typeof v === 'string' && v.length ? v : undefined)

// Persist selected calendar IDs per user/account in localStorage
const SELECTED_CALENDARS_KEY_PREFIX = 'dizee_selectedCalendars_'

const getSelectedCalendarsKey = (uid: string) => `${SELECTED_CALENDARS_KEY_PREFIX}${uid}`

const loadSelectedCalendarIds = (uid: string): string[] | null => {
  try {
    const raw = localStorage.getItem(getSelectedCalendarsKey(uid))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

const saveSelectedCalendarIds = (uid: string, ids: string[]) => {
  localStorage.setItem(getSelectedCalendarsKey(uid), JSON.stringify(ids))
}

const extractCalendarsFromPayload = (payload: unknown): GoogleCalendar[] => {
  const p = asRecord(payload)
  const d1 = asRecord(p?.['data'])
  const d2 = asRecord(d1?.['data'])
  const cals = d2?.['calendars'] ?? d1?.['calendars'] ?? p?.['calendars'] ?? []
  return Array.isArray(cals) ? (cals as GoogleCalendar[]) : []
}

const extractConnectedFromPayload = (payload: unknown): boolean => {
  const p = asRecord(payload)
  const d1 = asRecord(p?.['data'])
  const d2 = asRecord(d1?.['data'])
  const conn = asRecord(d2?.['connection'])
  const v = d2?.['connected'] ?? d1?.['connected'] ?? p?.['connected'] ?? conn?.['connected'] ?? false
  return Boolean(v)
}

const extractEventsFromPayload = (payload: unknown): GoogleCalendarEvent[] => {
  const p = asRecord(payload)
  const d1 = asRecord(p?.['data'])
  const d2 = asRecord(d1?.['data'])
  const events = d2?.['events'] ?? d1?.['events'] ?? p?.['events'] ?? d2?.['items'] ?? []
  return Array.isArray(events) ? (events as GoogleCalendarEvent[]) : []
}

const normalizeBookingsFromPayload = (payload: unknown): Show[] => {
  const p = asRecord(payload)
  const d1 = asRecord(p?.['data'])
  const d2 = asRecord(d1?.['data'])

  const list = (d2?.['data'] ?? d1?.['data'] ?? payload) as unknown
  const listRecord = asRecord(list)
  const arr = Array.isArray(list) ? list : Array.isArray(listRecord?.['data']) ? (listRecord?.['data'] as unknown[]) : []

  return arr
    .map((raw) => {
      const r = asRecord(raw) || {}
      return {
        id: toStr(r['_id']) || toStr(r['id']) || '',
        date: toStr(r['date']) || toStr(r['perf_date']),
        perf_date: toStr(r['perf_date']) || toStr(r['date']),
        title: toStr(r['event_title']) || toStr(r['title']) || toStr(r['venue']),
        event_title: toStr(r['event_title']) || toStr(r['title']),
        venue: toStr(r['venue']),
        city: toStr(r['city']),
        status: toStr(r['status']),
      }
    })
    .filter((s) => Boolean(s.id))
}

// Apply calendar selection filter to a raw event list.
// Returns only events belonging to the selected calendars.
const applyCalendarFilter = (
  events: GoogleCalendarEvent[],
  selectedIds: string[] | null,
  calendarList: GoogleCalendar[]
): GoogleCalendarEvent[] => {
  // No saved selection yet — first-time user, show everything
  if (selectedIds === null) return events
  // User explicitly selected nothing — hide all google events
  if (selectedIds.length === 0) return []
  // All calendars selected — no filtering needed
  if (calendarList.length > 0 && selectedIds.length === calendarList.length) return events

  return events.filter((evt) => {
    const calId = evt.calendarId || evt.organizer?.email
    if (!calId) return true // can't determine calendar — show it
    return selectedIds.includes(calId)
  })
}

export default function SchedulePage() {
  // Granular permissions — isViewOnly('schedule') can be used to gate edit actions
  const _permissions = useUserPermissions()
  void _permissions // available for future granular gating
  const userId = getEffectiveUserId() || 'self'
  const cachedBookings = readSessionCache<unknown>(makeCacheKey([userId, 'api', 'user/getBookings']))
  const cachedTasks = readSessionCache<unknown>(makeCacheKey([userId, 'api', 'tasks']))
  const cachedGcalStatus = readSessionCache<unknown>(makeCacheKey([userId, 'api', 'user/connectors/googleCalendar/status']))
  const cachedGcalEvents =
    readSessionCache<unknown>(makeCacheKey([userId, 'api', 'user/connectors/googleCalendar/events?maxResults=250'])) ??
    readSessionCache<unknown>(makeCacheKey([userId, 'api', 'user/connectors/googleCalendar/events?maxResults=50']))
  // Restore calendar list from session cache so filtering works from the first frame
  const cachedCalendars = readSessionCache<unknown>(makeCacheKey([userId, 'api', 'user/connectors/googleCalendar/calendars']))
  const initialCalendarList = cachedCalendars ? extractCalendarsFromPayload(cachedCalendars) : []
  const initialSelectedIds = loadSelectedCalendarIds(userId)

  const [shows, setShows] = useState<Show[]>(() => (cachedBookings ? normalizeBookingsFromPayload(cachedBookings) : []))
  const [tasks, setTasks] = useState<Task[]>(() => {
    if (!cachedTasks) return []
    const p = asRecord(cachedTasks)
    const d1 = asRecord(p?.['data'])
    const d2 = asRecord(d1?.['data'])
    const list = (d2 ?? d1 ?? p) as unknown
    return Array.isArray(list) ? (list as Task[]) : []
  })
  // Pre-filter cached events using the saved calendar selection so we never flash unfiltered data
  const [googleEvents, setGoogleEvents] = useState<GoogleCalendarEvent[]>(() => {
    if (!cachedGcalEvents) return []
    const allEvents = extractEventsFromPayload(cachedGcalEvents)
    return applyCalendarFilter(allEvents, initialSelectedIds, initialCalendarList)
  })
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [isGoogleConnected, setIsGoogleConnected] = useState(() => {
    if (cachedGcalStatus) return extractConnectedFromPayload(cachedGcalStatus)
    // If we have cached events but no status yet, assume connected.
    return Boolean(cachedGcalEvents && extractEventsFromPayload(cachedGcalEvents).length)
  })
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [isHoveringDisconnect, setIsHoveringDisconnect] = useState(false)

  // Calendar selection state — hydrate from caches immediately
  const [availableCalendars, setAvailableCalendars] = useState<GoogleCalendar[]>(initialCalendarList)
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<string[]>(() => {
    return initialSelectedIds ?? (initialCalendarList.length > 0 ? initialCalendarList.map((c) => c.id) : [])
  })
  const [isCalendarPickerOpen, setIsCalendarPickerOpen] = useState(false)
  const [hasInitializedCalendarSelection, setHasInitializedCalendarSelection] = useState(
    // If we already loaded from cache, we're initialized
    initialSelectedIds !== null || initialCalendarList.length > 0
  )

  const refreshGoogleStatusAndEvents = async () => {
    try {
      const status = await getDataAPI('user/connectors/googleCalendar/status')
      writeSessionCache(makeCacheKey([userId, 'api', 'user/connectors/googleCalendar/status']), status)
      const connected = extractConnectedFromPayload(status)
      setIsGoogleConnected(connected)

      if (connected) {
        // Fetch calendar list so user can pick which calendars to show
        const calendarsResp = await getDataAPI('user/connectors/googleCalendar/calendars').catch(() => ({
          data: { data: { calendars: [] } },
        }))
        writeSessionCache(makeCacheKey([userId, 'api', 'user/connectors/googleCalendar/calendars']), calendarsResp)
        const calList = extractCalendarsFromPayload(calendarsResp)
        setAvailableCalendars(calList)

        // If user has never made a selection, default to ALL calendars
        const savedIds = loadSelectedCalendarIds(userId)
        if (!savedIds && calList.length > 0 && !hasInitializedCalendarSelection) {
          const allIds = calList.map((c) => c.id)
          setSelectedCalendarIds(allIds)
          saveSelectedCalendarIds(userId, allIds)
          setHasInitializedCalendarSelection(true)
        }

        // Build calendarIds param for backend filtering (if supported)
        const currentSelectedIds = loadSelectedCalendarIds(userId)
        const calParam =
          currentSelectedIds && currentSelectedIds.length > 0
            ? `&calendarIds=${encodeURIComponent(currentSelectedIds.join(','))}`
            : ''
        const eventsResp = await getDataAPI(
          `user/connectors/googleCalendar/events?maxResults=250${calParam}`
        ).catch(() => ({
          data: { data: { events: [] } },
        }))
        writeSessionCache(makeCacheKey([userId, 'api', 'user/connectors/googleCalendar/events?maxResults=250']), eventsResp)
        // Also write a smaller key for pages that still read maxResults=50
        writeSessionCache(makeCacheKey([userId, 'api', 'user/connectors/googleCalendar/events?maxResults=50']), eventsResp)
        // Pre-filter before setting state so the UI never shows unfiltered data
        const allEvents = extractEventsFromPayload(eventsResp)
        setGoogleEvents(applyCalendarFilter(allEvents, currentSelectedIds, calList))
      } else {
        setGoogleEvents([])
        setAvailableCalendars([])
      }
    } catch (e) {
      console.error('Failed to load Google connection status:', e)
      setIsGoogleConnected(false)
      setGoogleEvents([])
    } finally {
      // Clear "connecting" UI state after first status refresh
      sessionStorage.removeItem('googleConnecting')
      setIsConnectingGoogle(false)
    }
  }

  useEffect(() => {
    if (sessionStorage.getItem('googleConnecting') === '1') {
      setIsConnectingGoogle(true)
    }
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [showsData, tasksData] = await Promise.all([
          getDataAPI('user/getBookings').catch(() => []),
          getDataAPI('tasks').catch(() => []),
        ])

        // Normalize shows
        const normalizedShows = normalizeBookingsFromPayload(showsData)
        writeSessionCache(makeCacheKey([userId, 'api', 'user/getBookings']), showsData)
        setShows(normalizedShows)
        writeSessionCache(makeCacheKey([userId, 'api', 'tasks']), tasksData)
        setTasks(tasksData || [])
        await refreshGoogleStatusAndEvents()
      } catch (error) {
        console.error('Error fetching schedule data:', error)
      }
    }
    fetchData()
  }, [])

  const handleConnectGoogle = async () => {
    setIsConnectingGoogle(true)
    try {
      sessionStorage.setItem('googleConnecting', '1')
      const redirectUri = `${window.location.origin}/google-calendar-callback`
      const resp = await getDataAPI(
        `user/connectors/googleCalendar/auth-url?redirectUri=${encodeURIComponent(redirectUri)}`
      )
      const authUrl =
        resp?.data?.data?.authUrl ?? resp?.data?.authUrl ?? resp?.authUrl ?? resp?.data?.data?.url ?? resp?.url
      if (!authUrl || typeof authUrl !== 'string') {
        window.alert('Failed to initiate Google connection.')
        return
      }
      window.location.href = authUrl
    } finally {
      setIsConnectingGoogle(false)
    }
  }

  const handleDisconnectGoogle = async () => {
    setIsDisconnecting(true)
    try {
      await postDataAPI('user/connectors/googleCalendar/disconnect', {})
      setIsGoogleConnected(false)
      setGoogleEvents([])
      setAvailableCalendars([])
      setSelectedCalendarIds([])
      setIsCalendarPickerOpen(false)
      // Clear cache
      sessionStorage.removeItem(makeCacheKey([userId, 'api', 'user/connectors/googleCalendar/status']))
      sessionStorage.removeItem(makeCacheKey([userId, 'api', 'user/connectors/googleCalendar/events?maxResults=250']))
      sessionStorage.removeItem(makeCacheKey([userId, 'api', 'user/connectors/googleCalendar/events?maxResults=50']))
      sessionStorage.removeItem(makeCacheKey([userId, 'api', 'user/connectors/googleCalendar/calendars']))
      localStorage.removeItem(getSelectedCalendarsKey(userId))
    } catch (e) {
      console.error('Failed to disconnect Google:', e)
      window.alert('Failed to disconnect Google. Please try again.')
    } finally {
      setIsDisconnecting(false)
      setIsHoveringDisconnect(false)
    }
  }

  // Calendar picker toggle & persistence
  const calendarPickerRef = useRef<HTMLDivElement>(null)

  const handleToggleCalendar = useCallback(
    (calId: string) => {
      setSelectedCalendarIds((prev) => {
        const next = prev.includes(calId) ? prev.filter((id) => id !== calId) : [...prev, calId]
        saveSelectedCalendarIds(userId, next)
        return next
      })
    },
    [userId]
  )

  const handleSelectAllCalendars = useCallback(() => {
    const allIds = availableCalendars.map((c) => c.id)
    setSelectedCalendarIds(allIds)
    saveSelectedCalendarIds(userId, allIds)
  }, [availableCalendars, userId])

  const handleDeselectAllCalendars = useCallback(() => {
    setSelectedCalendarIds([])
    saveSelectedCalendarIds(userId, [])
  }, [userId])

  // Re-fetch events when calendar selection changes (debounced to avoid rapid calls)
  const selectionChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasMountedRef = useRef(false)
  useEffect(() => {
    // Skip the very first render — events are already loaded via fetchData
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      return
    }
    if (!isGoogleConnected || availableCalendars.length === 0) return
    if (selectionChangeTimerRef.current) clearTimeout(selectionChangeTimerRef.current)
    selectionChangeTimerRef.current = setTimeout(() => {
      refreshGoogleStatusAndEvents()
    }, 400)
    return () => {
      if (selectionChangeTimerRef.current) clearTimeout(selectionChangeTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCalendarIds])

  // Close picker on outside click
  useEffect(() => {
    if (!isCalendarPickerOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (calendarPickerRef.current && !calendarPickerRef.current.contains(e.target as Node)) {
        setIsCalendarPickerOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isCalendarPickerOpen])

  // Track whether the user has ever saved a calendar preference for this account.
  // Once true it stays true for the lifetime of this component mount.
  const hasEverSavedSelection = useRef(initialSelectedIds !== null)
  // Keep ref in sync when the user makes their first selection
  useEffect(() => {
    if (!hasEverSavedSelection.current && selectedCalendarIds.length > 0 && availableCalendars.length > 0) {
      hasEverSavedSelection.current = true
    }
  }, [selectedCalendarIds, availableCalendars])

  // Client-side filtering: always respect the saved selection, even before the calendar
  // list finishes loading from the network. This prevents the "flash of all events" problem.
  const filteredGoogleEvents = useMemo(() => {
    const hadSelection = hasEverSavedSelection.current
    // User has explicitly deselected everything
    if (selectedCalendarIds.length === 0 && (availableCalendars.length > 0 || hadSelection)) return []
    // No saved selection AND no calendar list yet → first-time user, show everything
    if (!hadSelection && availableCalendars.length === 0) return googleEvents
    // All calendars selected — no filtering needed
    if (availableCalendars.length > 0 && selectedCalendarIds.length === availableCalendars.length) return googleEvents

    return googleEvents.filter((evt) => {
      const calId = evt.calendarId || evt.organizer?.email
      if (!calId) return true // can't determine calendar — show it
      return selectedCalendarIds.includes(calId)
    })
  }, [googleEvents, selectedCalendarIds, availableCalendars])

  // Combine all items into a unified schedule
  const scheduleItems = useMemo<ScheduleItem[]>(() => {
    const items: ScheduleItem[] = []

    // Add shows
    shows.forEach((show) => {
      const dateStr = show.perf_date || show.date
      if (!dateStr) return
      items.push({
        id: `show-${show.id}`,
        title: show.event_title || show.title || show.venue || 'Untitled Show',
        date: dateStr,
        type: 'show',
        venue: show.venue,
        city: show.city,
        status: show.status,
      })
    })

    // Add tasks with due dates
    tasks.forEach((task) => {
      if (!task.due_date) return
      items.push({
        id: `task-${task.id}`,
        title: task.title,
        date: task.due_date,
        type: 'task',
        status: task.status,
        priority: task.priority,
      })
    })

    // Add Google Calendar events (filtered by selected calendars)
    filteredGoogleEvents.forEach((event) => {
      const dateStr = event.start.dateTime || event.start.date
      if (!dateStr) return
      const dateOnly = dateStr.split('T')[0]
      const timeOnly = event.start.dateTime ? new Date(event.start.dateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : undefined
      items.push({
        id: `google-${event.id}`,
        title: event.summary || 'Untitled Event',
        date: dateOnly,
        time: timeOnly,
        type: 'google',
        location: event.location,
      })
    })

    // Sort by date
    return items.sort((a, b) => (parseLocalDate(a.date)?.getTime() ?? 0) - (parseLocalDate(b.date)?.getTime() ?? 0))
  }, [shows, tasks, filteredGoogleEvents])

  // Get items for a specific date (for calendar view)
  const getItemsForDate = (date: Date) => {
    const dateStr = toLocalDateKey(date)
    return scheduleItems.filter((item) => item.date === dateStr)
  }

  // Generate calendar days for current month view
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startPadding = firstDay.getDay()
    const totalDays = lastDay.getDate()

    const days: (Date | null)[] = []

    // Add padding for days before the first of the month
    for (let i = 0; i < startPadding; i++) {
      days.push(null)
    }

    // Add all days of the month
    for (let i = 1; i <= totalDays; i++) {
      days.push(new Date(year, month, i))
    }

    return days
  }, [currentMonth])

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev)
      if (direction === 'prev') {
        newMonth.setMonth(newMonth.getMonth() - 1)
      } else {
        newMonth.setMonth(newMonth.getMonth() + 1)
      }
      return newMonth
    })
  }

  const getCalendarItemStyle = (type: ScheduleItem['type']) => {
    switch (type) {
      case 'show':
        // Uses theme tokens via CSS vars so dark mode never looks “washed out”
        return 'bg-[rgba(var(--surface-2-rgb),0.95)] text-gray-800'
      case 'task':
        return 'bg-[rgba(var(--surface-2-rgb),0.75)] text-gray-600'
      case 'google':
        // Subtle Google-blue tint without turning light-gray in dark mode
        return 'bg-[rgba(66,133,244,0.12)] text-gray-600'
      default:
        return 'bg-[rgba(var(--surface-2-rgb),0.65)] text-gray-500'
    }
  }

  const getTypeBadge = (type: ScheduleItem['type']) => {
    switch (type) {
      case 'show':
        return 'Show'
      case 'task':
        return 'Task'
      case 'google':
        return 'Calendar'
      default:
        return type
    }
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const upcomingItems = scheduleItems.filter((item) => {
    const itemDate = parseLocalDate(item.date)
    if (!itemDate) return false
    itemDate.setHours(0, 0, 0, 0)
    return itemDate >= today
  })

  return (
    <div className="p-4 pt-4 md:p-8 max-w-6xl mx-auto">
      {/* Sticky page header */}
      <div className="sticky top-0 z-10 -mx-4 md:-mx-8 px-4 md:px-8 pt-16 md:pt-0 pb-4 bg-[rgb(var(--bg-rgb))]">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight mb-2">Schedule</h1>
            <p className="text-sm text-gray-500">Everything happening on your Dizee and Google Calendar</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
          {/* Google Calendar Connection */}
          {isGoogleConnected ? (
            <Button 
              variant="secondary" 
              onClick={handleDisconnectGoogle}
              onMouseEnter={() => setIsHoveringDisconnect(true)}
              onMouseLeave={() => setIsHoveringDisconnect(false)}
              disabled={isDisconnecting}
            >
              <span className="flex items-center gap-2 relative min-w-[160px] justify-center">
                {isDisconnecting ? (
                  <span className="flex items-center gap-2 transition-all duration-200 ease-in-out">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Disconnecting…
                  </span>
                ) : (
                  <>
                    <span 
                      className={`flex items-center gap-2 transition-all duration-200 ease-in-out ${
                        isHoveringDisconnect ? 'opacity-0 scale-95 absolute' : 'opacity-100 scale-100'
                      }`}
                    >
                      <svg className="w-4 h-4 text-green-600 transition-colors duration-200" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                      Google Connected
                    </span>
                    <span 
                      className={`flex items-center gap-2 transition-all duration-200 ease-in-out ${
                        isHoveringDisconnect ? 'opacity-100 scale-100' : 'opacity-0 scale-95 absolute'
                      }`}
                    >
                      <svg className="w-4 h-4 text-red-600 transition-colors duration-200" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                      </svg>
                      Disconnect
                    </span>
                  </>
                )}
              </span>
            </Button>
          ) : (
            <Button variant="secondary" onClick={handleConnectGoogle} disabled={isConnectingGoogle}>
              <span className="flex items-center gap-2">
                {isConnectingGoogle ? (
                  <>Connecting…</>
                ) : (
                  <>
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Connect Google
                  </>
                )}
              </span>
            </Button>
          )}

          {/* Calendar Filter — always shown when Google is connected */}
          {isGoogleConnected && (
            <div className="relative" ref={calendarPickerRef}>
              <Button
                variant="secondary"
                onClick={() => setIsCalendarPickerOpen((p) => !p)}
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  Calendars
                </span>
              </Button>

              {isCalendarPickerOpen && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg border border-gray-200 shadow-lg z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Show calendars
                    </span>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleSelectAllCalendars}
                        className="text-[11px] text-gray-500 hover:text-gray-700 transition-colors font-medium"
                      >
                        All
                      </button>
                      <button
                        onClick={handleDeselectAllCalendars}
                        className="text-[11px] text-gray-500 hover:text-gray-700 transition-colors font-medium"
                      >
                        None
                      </button>
                    </div>
                  </div>
                  {availableCalendars.length > 0 ? (
                    <div className="max-h-64 overflow-y-auto py-1">
                      {availableCalendars.map((cal) => {
                        const isSelected = selectedCalendarIds.includes(cal.id)
                        return (
                          <button
                            key={cal.id}
                            onClick={() => handleToggleCalendar(cal.id)}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
                          >
                            {/* Checkbox — matches Team member style */}
                            <div className="flex-shrink-0 relative flex items-center justify-center">
                              <div
                                className={`w-4 h-4 rounded border-2 transition-all duration-200 ease-in-out ${
                                  isSelected
                                    ? 'bg-gray-900 dark:bg-gray-100 border-gray-900 dark:border-gray-100'
                                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                                }`}
                              />
                              {isSelected && (
                                <svg className="absolute w-3 h-3 pointer-events-none" viewBox="0 0 20 20" fill="none">
                                  <path
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    className="fill-white dark:fill-gray-900"
                                  />
                                </svg>
                              )}
                            </div>
                            {/* Color dot */}
                            <div
                              className="w-3 h-3 rounded-full shrink-0"
                              style={{ backgroundColor: cal.backgroundColor || '#4285F4' }}
                            />
                            {/* Label */}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-gray-800 truncate">
                                {cal.summary}
                              </div>
                              {cal.description && (
                                <div className="text-[10px] text-gray-400 truncate mt-0.5">
                                  {cal.description}
                                </div>
                              )}
                            </div>
                            {cal.primary && (
                              <span className="text-[9px] text-gray-400 uppercase tracking-wider shrink-0">
                                Primary
                              </span>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="px-4 py-6 text-center">
                      <p className="text-xs text-gray-400">Loading calendars...</p>
                    </div>
                  )}
                  {availableCalendars.length > 0 && selectedCalendarIds.length === 0 && (
                    <div className="px-4 py-2 border-t border-gray-100 bg-amber-50">
                      <p className="text-[11px] text-amber-700">
                        No calendars selected — Google Calendar events won't appear.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* View Toggle */}
          <div className="bg-white rounded-lg border border-gray-200 p-1 flex items-center gap-1">
            <button
              onClick={() => setViewMode('list')}
              className={`
                flex items-center gap-2
                px-4 py-2
                text-sm
                rounded-md
                transition-colors
                ${viewMode === 'list'
                  ? 'bg-gray-50 text-gray-900 font-medium'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50/50'
                }
              `}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              List
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`
                flex items-center gap-2
                px-4 py-2
                text-sm
                rounded-md
                transition-colors
                ${viewMode === 'calendar'
                  ? 'bg-gray-50 text-gray-900 font-medium'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50/50'
                }
              `}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M5 11h14M6 21h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Calendar
            </button>
          </div>
        </div>
      </div>
      </div>

      {/* Summary Stats */}
      <div className="mb-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Upcoming</div>
          <div className="text-xl md:text-2xl font-semibold text-gray-900">{upcomingItems.length}</div>
          <div className="text-xs text-gray-500 mt-1 hidden sm:block">events scheduled</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Shows</div>
          <div className="text-xl md:text-2xl font-semibold text-gray-900">
            {upcomingItems.filter((i) => i.type === 'show').length}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Tasks Due</div>
          <div className="text-xl md:text-2xl font-semibold text-gray-900">
            {upcomingItems.filter((i) => i.type === 'task').length}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Calendar</div>
          <div className="text-xl md:text-2xl font-semibold text-gray-900">
            {upcomingItems.filter((i) => i.type === 'google').length}
          </div>
        </div>
      </div>

      {viewMode === 'list' ? (
        /* List View */
        <div className="space-y-2">
          {upcomingItems.length > 0 ? (
            upcomingItems.map((item, idx) => {
              const itemDate = parseLocalDate(item.date) || new Date()
              const isToday = itemDate.toDateString() === today.toDateString()
              const tomorrow = new Date(today.getTime() + 86400000)
              const isTomorrow = itemDate.toDateString() === tomorrow.toDateString()

              let dateLabel = itemDate.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              })
              if (isToday) dateLabel = 'Today'
              if (isTomorrow) dateLabel = 'Tomorrow'

              return (
                <div
                  key={item.id}
                  className={`rounded-lg p-4 flex items-center gap-3 hover:bg-gray-100/80 dark:hover:bg-white/[0.06] transition-colors ${idx % 2 === 0 ? 'bg-white dark:bg-[rgb(var(--surface-rgb))]' : 'bg-gray-50/60 dark:bg-white/[0.02]'}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                        {getTypeBadge(item.type)}
                      </span>
                      {item.status && (
                        <span className="text-[10px] text-gray-400">
                          · {item.status}
                        </span>
                      )}
                      {item.priority && (
                        <span className={`text-[10px] ${
                          item.priority === 'high' ? 'text-gray-600' : 'text-gray-400'
                        }`}>
                          · {item.priority}
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 truncate">{item.title}</h3>
                    {(item.venue || item.city || item.location) && (
                      <p className="text-xs text-gray-400 mt-0.5 truncate">
                        {item.venue && item.city
                          ? `${item.venue}, ${item.city}`
                          : item.venue || item.city || item.location}
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-sm font-medium ${isToday ? 'text-gray-900' : 'text-gray-600'}`}>
                      {dateLabel}
                    </div>
                    {item.time && (
                      <div className="text-xs text-gray-400 mt-0.5">{item.time}</div>
                    )}
                  </div>
                </div>
              )
            })
          ) : (
            <div className="bg-white rounded-lg p-12 text-center">
              <p className="text-sm text-gray-500">No upcoming events</p>
              <p className="text-xs text-gray-400 mt-1">Add shows, tasks, or connect your Google Calendar</p>
            </div>
          )}
        </div>
      ) : (
        /* Calendar View */
        <div className="ds-surface overflow-hidden">
          {/* Calendar Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h2 className="text-lg font-semibold text-gray-900">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h2>
            <button
              onClick={() => navigateMonth('next')}
              className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b border-gray-200">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="px-1 md:px-2 py-2 md:py-3 text-center text-[9px] md:text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                <span className="hidden sm:inline">{day}</span>
                <span className="sm:hidden">{day.charAt(0)}</span>
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7">
            {calendarDays.map((date, index) => {
              if (!date) {
                return <div key={`empty-${index}`} className="min-h-[60px] md:min-h-[100px] border-r border-b border-gray-200 bg-transparent" />
              }

              const dayItems = getItemsForDate(date)
              const isCurrentDay = date.toDateString() === today.toDateString()
              const isPastDay = date < today

              return (
                <div
                  key={toLocalDateKey(date)}
                  className={`min-h-[60px] md:min-h-[100px] p-1 md:p-2 border-r border-b border-gray-200 ${
                    isPastDay ? 'bg-[rgba(var(--surface-2-rgb),0.45)]' : 'bg-transparent'
                  }`}
                >
                  <div className={`text-xs md:text-sm font-medium mb-1 ${
                    isCurrentDay
                      ? 'w-5 h-5 md:w-7 md:h-7 flex items-center justify-center rounded-full bg-gray-900 text-white text-[10px] md:text-sm'
                      : isPastDay
                      ? 'text-gray-500'
                      : 'text-gray-900'
                  }`}>
                    {date.getDate()}
                  </div>
                  <div className="space-y-0.5 md:space-y-1">
                    {dayItems.slice(0, window.innerWidth < 640 ? 1 : 3).map((item) => (
                      <div
                        key={item.id}
                        className={`text-[9px] md:text-[11px] px-1 md:px-1.5 py-0.5 rounded truncate ${getCalendarItemStyle(item.type)}`}
                        title={item.title}
                      >
                        {item.title}
                      </div>
                    ))}
                    {dayItems.length > (window.innerWidth < 640 ? 1 : 3) && (
                      <div className="text-[9px] md:text-[11px] text-gray-400 px-1 md:px-1.5">+{dayItems.length - (window.innerWidth < 640 ? 1 : 3)} more</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
